import type { SocialPostSceneBlockV1, SocialPostScenePlanV1 } from "@runcheetah/shared";

export type CreativeLayerEntry = {
  block: SocialPostSceneBlockV1;
};

export type CreativeLayerReorderUpdate = {
  blockId: string;
  changes: { zIndex: number };
};

export function getSortedCreativeLayerEntries(
  plan: SocialPostScenePlanV1 | null,
): CreativeLayerEntry[] {
  if (!plan) {
    return [];
  }

  return plan.scene.blocks.toSorted(sortFrontToBack).map((block) => ({ block }));
}

export function getVisibleCreativeLayerEntries(
  entries: CreativeLayerEntry[],
): CreativeLayerEntry[] {
  return entries;
}

export function createCreativeLayerReorderUpdates(input: {
  blocks: SocialPostSceneBlockV1[];
  activeBlockId: string;
  overBlockId: string;
}): CreativeLayerReorderUpdate[] {
  if (
    !input.blocks.some((block) => block.id === input.activeBlockId) ||
    !input.blocks.some((block) => block.id === input.overBlockId)
  ) {
    return [];
  }

  const siblings = input.blocks.toSorted(sortFrontToBack);
  const activeIndex = siblings.findIndex((block) => block.id === input.activeBlockId);
  const overIndex = siblings.findIndex((block) => block.id === input.overBlockId);

  if (activeIndex < 0 || overIndex < 0 || activeIndex === overIndex) {
    return [];
  }

  return moveArrayItem(siblings, activeIndex, overIndex).flatMap((block, index, reordered) => {
    const nextZIndex = reordered.length - 1 - index;

    return block.zIndex === nextZIndex
      ? []
      : [{ blockId: block.id, changes: { zIndex: nextZIndex } }];
  });
}

export function createCreativeLayerCompactionUpdates(
  blocks: SocialPostSceneBlockV1[],
): CreativeLayerReorderUpdate[] {
  return createZIndexUpdates(blocks.toSorted(sortFrontToBack));
}

export function createCreativeLayerMoveStepUpdates(input: {
  blocks: SocialPostSceneBlockV1[];
  blockId: string;
  direction: "forward" | "backward";
}): CreativeLayerReorderUpdate[] {
  const sortedBlocks = input.blocks.toSorted(sortFrontToBack);
  const currentIndex = sortedBlocks.findIndex((block) => block.id === input.blockId);

  if (currentIndex < 0) {
    return [];
  }

  const nextIndex = input.direction === "forward" ? currentIndex - 1 : currentIndex + 1;
  if (nextIndex < 0 || nextIndex >= sortedBlocks.length) {
    return [];
  }

  return createZIndexUpdates(moveArrayItem(sortedBlocks, currentIndex, nextIndex));
}

export function createCreativeLayerMoveEdgeUpdates(input: {
  blocks: SocialPostSceneBlockV1[];
  blockId: string;
  edge: "front" | "back";
}): CreativeLayerReorderUpdate[] {
  const sortedBlocks = input.blocks.toSorted(sortFrontToBack);
  const currentIndex = sortedBlocks.findIndex((block) => block.id === input.blockId);

  if (currentIndex < 0) {
    return [];
  }

  const nextIndex = input.edge === "front" ? 0 : sortedBlocks.length - 1;
  if (currentIndex === nextIndex) {
    return [];
  }

  return createZIndexUpdates(moveArrayItem(sortedBlocks, currentIndex, nextIndex));
}

function createZIndexUpdates(sortedFrontToBackBlocks: SocialPostSceneBlockV1[]) {
  return sortedFrontToBackBlocks.flatMap((block, index, sortedBlocks) => {
    const nextZIndex = sortedBlocks.length - 1 - index;

    return block.zIndex === nextZIndex
      ? []
      : [{ blockId: block.id, changes: { zIndex: nextZIndex } }];
  });
}

function moveArrayItem<T>(items: T[], fromIndex: number, toIndex: number): T[] {
  const nextItems = [...items];
  const [item] = nextItems.splice(fromIndex, 1);

  if (!item) {
    return items;
  }

  nextItems.splice(toIndex, 0, item);
  return nextItems;
}

function sortFrontToBack(left: SocialPostSceneBlockV1, right: SocialPostSceneBlockV1) {
  return right.zIndex - left.zIndex;
}
